import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { authAPI, permissionsAPI } from '../services/api.ts';
import { User } from '../types';
import { isTokenExpiringWithin, getTokenTimeRemaining } from '../utils/tokenUtils.ts';
import { message } from 'antd';

export type PermissionValue = 'none' | 'read' | 'write';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  permissions: Record<string, PermissionValue>;
  canRead: (resource: string) => boolean;
  canWrite: (resource: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<Record<string, PermissionValue>>({});
  const tokenCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Carica i permessi effettivi del livello utente (menu/pulsanti).
  // In caso di errore si lascia la mappa vuota: default 'write' (fail-open),
  // tanto il backend applica comunque i permessi con 403 sulle API.
  const loadPermissions = async () => {
    try {
      const data = await permissionsAPI.getMyPermissions();
      setPermissions(data.permissions || {});
    } catch (error) {
      console.error('Errore caricamento permessi:', error);
      setPermissions({});
    }
  };

  // Permesso mancante nella mappa = 'write' (stesso default del backend)
  const canRead = (resource: string) => (permissions[resource] ?? 'write') !== 'none';
  const canWrite = (resource: string) => (permissions[resource] ?? 'write') === 'write';

  // Funzione per effettuare il refresh proattivo del token
  const proactiveTokenRefresh = async () => {
    const token = localStorage.getItem('token');
    if (!token || !user) {
      return;
    }

    try {
      // Controlla se il token scadrà entro 10 minuti (600 secondi)
      if (isTokenExpiringWithin(token, 600)) {
        console.log('Token is expiring soon, refreshing...');
        const response = await authAPI.refreshToken();
        
        if (response.access_token) {
          localStorage.setItem('token', response.access_token);
          console.log(`Token refreshed successfully. New token expires in ${response.expires_in} seconds`);
        }
      }
    } catch (error) {
      console.error('Proactive token refresh failed:', error);
      // Non forziamo il logout qui, lasciamo che l'interceptor gestisca il caso
    }
  };

  // Avvia il monitoraggio del token
  const startTokenMonitoring = () => {
    // Pulisci l'intervallo esistente se presente
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
    }

    // Controlla ogni 5 minuti (300000ms)
    tokenCheckIntervalRef.current = setInterval(() => {
      proactiveTokenRefresh();
    }, 300000);

    // Controllo immediato
    proactiveTokenRefresh();
  };

  // Ferma il monitoraggio del token
  const stopTokenMonitoring = () => {
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
      tokenCheckIntervalRef.current = null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      console.log('🔄 AuthContext: Inizializzazione autenticazione...');
      const token = localStorage.getItem('token');
      
      if (token) {
        console.log('🔑 AuthContext: Token trovato, verifico utente corrente...');
        try {
          // Timeout specifico per l'inizializzazione
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout inizializzazione')), 5000)
          );
          
          const userData = await Promise.race([
            authAPI.getCurrentUser(),
            timeoutPromise
          ]);
          
          console.log('✅ AuthContext: Utente autenticato:', userData.username);
          setUser(userData);
          await loadPermissions();
        } catch (error: any) {
          console.error('❌ AuthContext: Errore verifica utente:', error.message || error);
          
          // Gestione semplificata degli errori
          if (error.message === 'Timeout inizializzazione') {
            console.log('⏰ AuthContext: Timeout inizializzazione');
            message.warning('Connessione lenta, riprova tra qualche secondo');
          } else if (error.response?.status === 401) {
            console.log('🔐 AuthContext: Token non valido - logout necessario');
            localStorage.removeItem('token');
          } else {
            // Rete assente, backend in riavvio (503), errore del server: la sessione
            // è ancora valida, non va buttata via per un disservizio transitorio
            console.log('⚠️ AuthContext: Errore temporaneo durante la verifica utente - token conservato');
            message.warning('Server non raggiungibile: ricarica la pagina tra qualche secondo');
          }
        }
      } else {
        console.log('🔓 AuthContext: Nessun token trovato');
      }
      
      console.log('✅ AuthContext: Inizializzazione completata');
      setLoading(false);
    };

    initAuth();
  }, []);

  // Gestisci il monitoraggio del token quando l'utente cambia
  useEffect(() => {
    if (user) {
      startTokenMonitoring();
    } else {
      stopTokenMonitoring();
    }

    // Cleanup al dismount
    return () => {
      stopTokenMonitoring();
    };
  }, [user]);

  const login = async (username: string, password: string) => {
    try {
      const response = await authAPI.login(username, password);
      
      if (response.access_token) {
        localStorage.setItem('token', response.access_token);
        
        // Ottieni i dati dell'utente dopo il login
        const userData = await authAPI.getCurrentUser();
        setUser(userData);
        await loadPermissions();
      } else {
        throw new Error('Login response is missing access_token');
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    stopTokenMonitoring();
    try {
      await authAPI.logout();
    } catch {
      // Il token potrebbe già essere scaduto; si procede comunque con il logout locale
    }
    localStorage.removeItem('token');
    setUser(null);
    setPermissions({});
  };

  const value = {
    user,
    loading,
    login,
    logout,
    permissions,
    canRead,
    canWrite,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
